import type AHDate from "_shared/dates/date";
import { formatDate, formatDateRange } from "_shared/formatting/dates";
import { ProposalOrderField } from "work/proposals/__constants/proposal-lifecycle";

/**
 * What the proposals table's Dates column says: when a response is due, and the term the work runs.
 *
 * Response due, Start date and End date were three columns of their own, 310px between them, and
 * two of them were pushed past the table's right edge at most widths (#6512). Together they fit in
 * one column of two lines. The month is spelled out: "Oct 3 – Dec 9, 2026" is shorter than two
 * numeric dates and cannot be read the wrong way round.
 */
export type ProposalDatesSummary = {
  /** "Due Oct 3, 2026", when the proposal asks for a response by a date. */
  due?: string;
  /** "Oct 3 – Dec 9, 2026", "From Oct 3, 2026" or "Until Dec 9, 2026", when it has a term. */
  term?: string;
};

export function summarizeProposalDates(
  responseDueBy: AHDate | null | undefined,
  startDate: AHDate | null | undefined,
  endDate: AHDate | null | undefined,
): ProposalDatesSummary {
  const due = formatDate(responseDueBy);
  return {
    due: due ? `Due ${due}` : undefined,
    term: termOf(startDate, endDate),
  };
}

function termOf(startDate: AHDate | null | undefined, endDate: AHDate | null | undefined): string | undefined {
  // The year is said once when the term sits inside one.
  const range = formatDateRange(startDate, endDate);
  if (range) return range;
  const start = formatDate(startDate);
  if (start) return `From ${start}`;
  const end = formatDate(endDate);
  if (end) return `Until ${end}`;
  return undefined;
}

/**
 * The dates the Dates column's menu sorts by, in the order it offers them. Each `id` is the sort
 * the page reads from and writes to the URL, so it is the order field's name in lower case.
 */
export const proposalDateSorts: ReadonlyArray<{ id: string; label: string }> = [
  { id: ProposalOrderField.ResponseDueBy.toLowerCase(), label: "response due" },
  { id: ProposalOrderField.Start.toLowerCase(), label: "start date" },
  { id: ProposalOrderField.End.toLowerCase(), label: "end date" },
];
